import React, { useCallback, useMemo, useRef } from 'react';
import MultiProvider from '@modules/miscellaneous/common/components/MultiProvider';
import { AssistantPage as TAssistantPage } from 'types';
import { StaticTranslationProvider } from '@rbx/intl';
import CodeStylesProvider from '@modules/ui/codeBlock/CodeStylesProvider';
import { getLocaleInfoForLocale } from '@modules/localization/implementations/translationResourceHelpers';
import Head from 'next/head';
import AppRootLayout from '@modules/layout/layout/components/AppRootLayout';
import { EStudioTaskType, StudioResourcesProvider } from '@rbx/studio';
import { createStudioResourcesWithTheme } from '@modules/useStudio/constants/studioResources';
import { IxpLayers, fetchIxpParameters } from '@modules/clients/ixpExperiments';
import { useQuery } from '@tanstack/react-query';
import { Grid, CircularProgress } from '@rbx/ui';
import SearchContainer from '@modules/ui/search/SearchContainer';
import { CreatorHubLayout } from '@rbx/creator-hub-navigation';
import useOpenStudio from '@modules/useStudio/hooks/useOpenStudio';
import PageBreadcrumbs from '@modules/ui/shared/components/PageBreadcrumbs';
import useNavigationSearch from '@modules/layout/topNavigation/hooks/useNavigationSearch';
import useCurrentLocale from '@modules/localization/hooks/useCurrentLocale';
import { useThemeMode } from '@rbx/settings';
import AppConfigProvider from '@modules/layout/layout/components/AppConfigProvider';
import { ESearchInteraction } from '@modules/eventStream/enum/DocsSiteSearch';
import DocsAssistantContextProvider from '../context/DocsAssistantContextProvider';
import Assistant from './Assistant';
import { AssistantOptInProvider } from '../context/AssistantOptInProvider';

const AssistantPage: TAssistantPage = ({
  translationResourcesByLocale,
  defaultTranslationResources,
  navigation,
  urlBasePath,
}) => {
  const { targetLocale } = useCurrentLocale();
  const { open, dialog } = useOpenStudio();
  const layoutContainerRef = useRef<HTMLDivElement>(null);
  const { setSearchDialogOpenWithEvent } = useNavigationSearch();
  const { data, isFetched } = useQuery({
    queryKey: ['ixp', IxpLayers.CreatorHubNavigationUser],
    queryFn: () => fetchIxpParameters(IxpLayers.CreatorHubNavigationUser),
  });
  const { themeMode } = useThemeMode();

  const locale = targetLocale;
  const translationResources = translationResourcesByLocale[locale] ?? defaultTranslationResources;
  const localeInfo = getLocaleInfoForLocale(locale);

  const scrollToBottom = useCallback(() => {
    if (layoutContainerRef?.current) {
      layoutContainerRef.current.scrollTo({
        top: layoutContainerRef.current.scrollHeight,
      });
    }
  }, []);

  const openStudio = useCallback(() => {
    open({ task: EStudioTaskType.Default });
  }, [open]);

  const breadcrumbs = useMemo(() => [{ label: 'Learn', path: '/' }, { label: 'Assistant' }], []);

  if (!isFetched) {
    return (
      <Grid container alignContent='center' justifyContent='center' height='100vh'>
        <CircularProgress />
      </Grid>
    );
  }

  if (data?.enableIAM2) {
    return (
      <MultiProvider
        providers={[
          /* eslint-disable react/jsx-key */
          <StaticTranslationProvider localeInfo={localeInfo} resources={translationResources} />,
          <AssistantOptInProvider />,
          <DocsAssistantContextProvider />,
          <StudioResourcesProvider resources={createStudioResourcesWithTheme(themeMode)} />,
          <CodeStylesProvider fullHeight hideHeaderInSingleLine />,
          /* eslint-enable react/jsx-key */
        ]}>
        <Head>
          <meta property='og:locale' content={locale} />
        </Head>
        <CreatorHubLayout>
          <CreatorHubLayout.Rail openStudio={openStudio} />
          <CreatorHubLayout.Header
            onSearchClick={() =>
              setSearchDialogOpenWithEvent({
                searchDialogOpen: true,
                interaction: ESearchInteraction.NavSearchIcon,
              })
            }>
            <SearchContainer />
            <PageBreadcrumbs items={breadcrumbs} enableNewLayout />
          </CreatorHubLayout.Header>
          <CreatorHubLayout.PageContent ref={layoutContainerRef}>
            {dialog}
            <Assistant enableIAM2 scrollToBottom={scrollToBottom} />
          </CreatorHubLayout.PageContent>
        </CreatorHubLayout>
      </MultiProvider>
    );
  }

  return (
    <MultiProvider
      providers={[
        /* eslint-disable react/jsx-key */
        <StaticTranslationProvider localeInfo={localeInfo} resources={translationResources} />,
        <AssistantOptInProvider />,
        <DocsAssistantContextProvider />,
        <AppConfigProvider
          breadcrumbs={[]}
          menuData={[]}
          hideInPageNavigation={false}
          hideBreadcrumbs={false}
          isOpenSourced={false}
          blockSearchIndexing
          locale={targetLocale}
          pageMaxWidth={0}
          machineTranslated={false}
          isLandingPage={false}
          hideSecondaryNav={false}
        />,
        <AppRootLayout isStickyHeader={false} navigation={navigation} urlBasePath={urlBasePath} />,
        <CodeStylesProvider fullHeight hideHeaderInSingleLine />,
        <StudioResourcesProvider resources={createStudioResourcesWithTheme(themeMode)} />,
        /* eslint-enable react/jsx-key */
      ]}>
      <Head>
        <meta property='og:locale' content={locale} />
      </Head>
      <Assistant />
    </MultiProvider>
  );
};

export default AssistantPage;
